import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Crown, TrendingUp, Shield, RefreshCw, Search,
  ChevronDown, ChevronUp, Edit2, Check, X, LogOut,
  Activity, Calendar, Mail, Smartphone, Globe, Lock,
  AlertCircle, CheckCircle, Clock, Zap, BarChart2, Eye
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

const ADMIN_EMAILS = ['sandeep@investorq.io', 'aryansingh@investorq.io', 'asingh10@babson.edu', 'sandeep@offbeet.in'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const timeAgo = (d) => {
  if (!d) return '—';
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

const tierConfig = {
  premium: { label: 'Premium', bg: '#3B0764', color: '#C084FC', border: '#7C3AED' },
  pro:     { label: 'Pro',     bg: '#451A03', color: '#FCD34D', border: '#D97706' },
  free:    { label: 'Free',    bg: '#0F172A', color: '#64748B', border: '#334155' },
};

const TierBadge = ({ tier }) => {
  const cfg = tierConfig[tier] || tierConfig.free;
  return (
    <span style={{
      padding: '2px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 700,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      letterSpacing: '0.05em', textTransform: 'uppercase'
    }}>{cfg.label}</span>
  );
};

const StatusDot = ({ active }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
    <span style={{
      width: 7, height: 7, borderRadius: '50%',
      background: active ? '#22C55E' : '#EF4444',
      boxShadow: active ? '0 0 6px #22C55E88' : 'none'
    }} />
    <span style={{ fontSize: '0.75rem', color: active ? '#86EFAC' : '#FCA5A5' }}>
      {active ? 'Active' : 'Inactive'}
    </span>
  </span>
);

// ─── Login Screen ──────────────────────────────────────────────────────────────

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (!ADMIN_EMAILS.includes(email.toLowerCase())) {
        setError('Access denied. Admin accounts only.'); setLoading(false); return;
      }
      // For Google auth accounts, skip password check and just verify email is admin
      // Try email/password login first
      const res = await fetch(`${API_BASE}/users/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      // Accept either successful login OR OTP-required response (both mean credentials valid)
      if (!res.ok && !data.requiresOTP) {
        // If Google auth error, allow access anyway since email is in admin list
        if (data.error && data.error.includes('google')) {
          onLogin(email, '');
          setLoading(false);
          return;
        }
        setError(data.error || 'Login failed'); setLoading(false); return;
      }
      onLogin(email, data.token || '');
    } catch {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#030712', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
      fontFamily: "'DM Sans', -apple-system, sans-serif"
    }}>
      <div style={{
        width: '100%', maxWidth: 380, padding: '2rem',
        background: 'linear-gradient(145deg, #0F172A, #1E293B)',
        border: '1px solid #1E3A5F', borderRadius: 16,
        boxShadow: '0 25px 60px rgba(0,0,0,0.6)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14, margin: '0 auto 1rem',
            background: 'linear-gradient(135deg, #1D4ED8, #7C3AED)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Shield size={24} color="white" />
          </div>
          <h1 style={{ color: '#F1F5F9', fontSize: '1.4rem', fontWeight: 700, margin: 0 }}>Admin Portal</h1>
          <p style={{ color: '#64748B', fontSize: '0.85rem', margin: '0.4rem 0 0' }}>InvestorQ Internal Dashboard</p>
        </div>

        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 8, padding: '0.75rem', marginBottom: '1rem',
            color: '#FCA5A5', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 6
          }}>
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <form onSubmit={handleLogin}>
          {[
            { label: 'Email', value: email, set: setEmail, type: 'email', Icon: Mail, placeholder: 'admin@investorq.io' },
            { label: 'Password', value: password, set: setPassword, type: 'password', Icon: Lock, placeholder: '••••••••' }
          ].map(({ label, value, set, type, Icon, placeholder }) => (
            <div key={label} style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', color: '#94A3B8', fontSize: '0.8rem', marginBottom: 6 }}>{label}</label>
              <div style={{ position: 'relative' }}>
                <Icon size={15} color="#475569" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                <input type={type} value={value} onChange={e => set(e.target.value)} placeholder={placeholder} required
                  style={{
                    width: '100%', padding: '0.7rem 0.75rem 0.7rem 2.4rem',
                    background: 'rgba(15,23,42,0.8)', border: '1px solid #1E3A5F',
                    borderRadius: 8, color: '#F1F5F9', fontSize: '0.875rem',
                    outline: 'none', boxSizing: 'border-box'
                  }} />
              </div>
            </div>
          ))}
          <button type="submit" disabled={loading} style={{
            width: '100%', padding: '0.8rem',
            background: loading ? '#334155' : 'linear-gradient(135deg, #1D4ED8, #7C3AED)',
            border: 'none', borderRadius: 8, color: 'white',
            fontSize: '0.9rem', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
            marginTop: '0.5rem'
          }}>
            {loading ? 'Signing in...' : 'Access Dashboard →'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ─── Stat Card ─────────────────────────────────────────────────────────────────

const StatCard = ({ label, value, sub, icon: Icon, color, bg }) => (
  <div style={{
    background: 'linear-gradient(145deg, #0F172A, #1E293B)',
    border: '1px solid #1E293B', borderRadius: 12, padding: '0.875rem',
    display: 'flex', alignItems: 'flex-start', gap: '0.75rem'
  }}>
    <div style={{
      width: 42, height: 42, borderRadius: 10, flexShrink: 0,
      background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <Icon size={20} color={color} />
    </div>
    <div>
      <div style={{ color: '#94A3B8', fontSize: '0.75rem', marginBottom: 2 }}>{label}</div>
      <div style={{ color: '#F1F5F9', fontSize: '1.6rem', fontWeight: 800, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ color: '#64748B', fontSize: '0.72rem', marginTop: 4 }}>{sub}</div>}
    </div>
  </div>
);

// ─── Grant Modal ───────────────────────────────────────────────────────────────

const GrantModal = ({ user, onClose, onGrant, adminEmail }) => {
  const [tier, setTier] = useState('pro');
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleGrant = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/subscription/admin/grant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-email': adminEmail },
        body: JSON.stringify({ userId: user.id, tier, durationDays: days })
      });
      if (res.ok) { setDone(true); setTimeout(() => { onGrant(); onClose(); }, 1200); }
    } catch { /* silent */ }
    setLoading(false);
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: 360, background: 'linear-gradient(145deg,#0F172A,#1E293B)',
        border: '1px solid #334155', borderRadius: 14, padding: '1.75rem'
      }}>
        <h3 style={{ color: '#F1F5F9', margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: 700 }}>
          Grant Subscription
        </h3>
        <p style={{ color: '#64748B', fontSize: '0.8rem', margin: '0 0 1.25rem' }}>
          {user.name} — {user.email}
        </p>

        <label style={{ display: 'block', color: '#94A3B8', fontSize: '0.78rem', marginBottom: 6 }}>Tier</label>
        <select value={tier} onChange={e => setTier(e.target.value)} style={{
          width: '100%', padding: '0.6rem 0.75rem', marginBottom: '1rem',
          background: '#0F172A', border: '1px solid #334155', borderRadius: 8,
          color: '#F1F5F9', fontSize: '0.875rem', outline: 'none'
        }}>
          <option value="pro">Pro</option>
          <option value="premium">Premium</option>
        </select>

        <label style={{ display: 'block', color: '#94A3B8', fontSize: '0.78rem', marginBottom: 6 }}>Duration (days)</label>
        <select value={days} onChange={e => setDays(parseInt(e.target.value))} style={{
          width: '100%', padding: '0.6rem 0.75rem', marginBottom: '1.5rem',
          background: '#0F172A', border: '1px solid #334155', borderRadius: 8,
          color: '#F1F5F9', fontSize: '0.875rem', outline: 'none'
        }}>
          {[30,60,90,180,365].map(d => <option key={d} value={d}>{d} days</option>)}
        </select>

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '0.7rem', background: '#1E293B', border: '1px solid #334155',
            borderRadius: 8, color: '#94A3B8', cursor: 'pointer', fontSize: '0.85rem'
          }}>Cancel</button>
          <button onClick={handleGrant} disabled={loading || done} style={{
            flex: 1, padding: '0.7rem',
            background: done ? '#166534' : 'linear-gradient(135deg,#7C3AED,#1D4ED8)',
            border: 'none', borderRadius: 8, color: 'white',
            cursor: loading || done ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: 600
          }}>
            {done ? '✓ Granted!' : loading ? 'Granting...' : 'Grant Access'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Admin Panel ──────────────────────────────────────────────────────────

const AdminPanel = ({ user }) => {
  const [adminEmail, setAdminEmail] = useState(null);
  const isMobile = window.innerWidth < 768;
  const [adminToken, setAdminToken] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [authFilter, setAuthFilter] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [grantUser, setGrantUser] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Auto-login if current user is admin (from props or localStorage)
  useEffect(() => {
    if (user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase())) {
      setAdminEmail(user.email);
      fetchUsers(user.email);
      return;
    }
    try {
      const stored = localStorage.getItem('investorq_user');
      if (stored) {
        const u = JSON.parse(stored);
        if (u?.email && ADMIN_EMAILS.includes(u.email.toLowerCase())) {
          setAdminEmail(u.email);
          fetchUsers(u.email);
        }
      }
    } catch {}
  }, [user]);

  const fetchUsers = useCallback(async (emailOverride) => {
    const email = emailOverride || adminEmail;
    if (!email) return;
    setLoading(true);
    try {
      const token = adminToken || localStorage.getItem('investorq_token') || '';
      const res = await fetch(`${API_BASE}/users/all`, {
        headers: {
          'x-admin-email': email,
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
      } else {
        setUsers([]);
      }
    } catch { setUsers([]); }
    setLastRefresh(new Date());
    setLoading(false);
  }, [adminEmail, adminToken]);

  useEffect(() => { if (adminEmail) fetchUsers(adminEmail); }, [adminEmail]);

  if (!adminEmail) return <AdminLogin onLogin={(email, token) => { setAdminEmail(email); setAdminToken(token); }} />;

  // ── Stats ──
  const total    = users.length;
  const premium  = users.filter(u => u.subscription_tier === 'premium').length;
  const pro      = users.filter(u => u.subscription_tier === 'pro').length;
  const free     = users.filter(u => u.subscription_tier === 'free').length;
  const google   = users.filter(u => u.auth_provider === 'google').length;
  const emailReg = users.filter(u => u.auth_provider === 'email').length;
  const thisWeek = users.filter(u => {
    const d = new Date(u.created_at);
    return (Date.now() - d.getTime()) < 7 * 86400000;
  }).length;

  // ── Filter + sort ──
  const filtered = users
    .filter(u => {
      const q = search.toLowerCase();
      const matchSearch = !q || u.email?.toLowerCase().includes(q) || u.name?.toLowerCase().includes(q);
      const matchTier = tierFilter === 'all' || u.subscription_tier === tierFilter;
      const matchAuth = authFilter === 'all' || u.auth_provider === authFilter;
      return matchSearch && matchTier && matchAuth;
    })
    .sort((a, b) => {
      let av = a[sortField] || '', bv = b[sortField] || '';
      if (sortField === 'created_at') { av = new Date(av); bv = new Date(bv); }
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });

  const toggleSort = (f) => { if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(f); setSortDir('asc'); } };
  const SortIcon = ({ field }) => sortField === field ? (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />) : null;

  const stats = [
    { label: 'Total Users',    value: total,    sub: `+${thisWeek} this week`,   icon: Users,      color: '#60A5FA', bg: 'rgba(96,165,250,0.1)' },
    { label: 'Premium',        value: premium,  sub: `${((premium/total||0)*100).toFixed(0)}% of users`, icon: Crown, color: '#C084FC', bg: 'rgba(192,132,252,0.1)' },
    { label: 'Pro',            value: pro,      sub: `${((pro/total||0)*100).toFixed(0)}% of users`,     icon: Zap,   color: '#FCD34D', bg: 'rgba(252,211,77,0.1)' },
    { label: 'Free',           value: free,     sub: 'Conversion opportunity',   icon: Users,      color: '#94A3B8', bg: 'rgba(148,163,184,0.1)' },
    { label: 'Google Auth',    value: google,   sub: `${emailReg} via email`,    icon: Globe,      color: '#34D399', bg: 'rgba(52,211,153,0.1)' },
    { label: 'New This Week',  value: thisWeek, sub: 'Last 7 days',              icon: TrendingUp, color: '#F87171', bg: 'rgba(248,113,113,0.1)' },
  ];

  return (
    <div style={{
      minHeight: '100vh', background: '#030712',
      fontFamily: "'DM Sans', -apple-system, sans-serif", color: '#F1F5F9'
    }}>
      {/* Header */}
      <div style={{
        borderBottom: '1px solid #0F172A', padding: isMobile ? '0.6rem 0.75rem' : '0.875rem 1.5rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem',
        background: 'linear-gradient(90deg,#030712,#0F172A)',
        position: 'sticky', top: 0, zIndex: 100
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{
            width: 34, height: 34, borderRadius: 9,
            background: 'linear-gradient(135deg,#1D4ED8,#7C3AED)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Shield size={17} color="white" />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>InvestorQ Admin</div>
            <div style={{ color: '#475569', fontSize: '0.7rem' }}>{adminEmail}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {lastRefresh && (
            <span style={{ color: '#475569', fontSize: '0.72rem' }}>
              Updated {timeAgo(lastRefresh)}
            </span>
          )}
          <button onClick={fetchUsers} disabled={loading} style={{
            display: 'flex', alignItems: 'center', gap: 5, padding: '0.45rem 0.85rem',
            background: '#0F172A', border: '1px solid #1E3A5F', borderRadius: 7,
            color: '#60A5FA', cursor: 'pointer', fontSize: '0.8rem'
          }}>
            <RefreshCw size={13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            Refresh
          </button>
          <button onClick={() => setAdminEmail(null)} style={{
            display: 'flex', alignItems: 'center', gap: 5, padding: '0.45rem 0.85rem',
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
            borderRadius: 7, color: '#F87171', cursor: 'pointer', fontSize: '0.8rem'
          }}>
            <LogOut size={13} /> Sign Out
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1300, margin: '0 auto', padding: isMobile ? '0.75rem' : '1.5rem' }}>

        {/* Stats Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(auto-fit,minmax(180px,1fr))', gap: isMobile ? '0.5rem' : '0.875rem', marginBottom: '1.5rem' }}>
          {stats.map(s => <StatCard key={s.label} {...s} />)}
        </div>

        {/* Tier breakdown bar */}
        {total > 0 && (
          <div style={{
            background: 'linear-gradient(145deg,#0F172A,#1E293B)',
            border: '1px solid #1E293B', borderRadius: 12, padding: '1rem 1.25rem',
            marginBottom: '1.75rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
              <span style={{ color: '#94A3B8', fontSize: '0.78rem', fontWeight: 600 }}>TIER DISTRIBUTION</span>
              <span style={{ color: '#64748B', fontSize: '0.72rem' }}>{total} total users</span>
            </div>
            <div style={{ height: 8, borderRadius: 4, overflow: 'hidden', display: 'flex', gap: 2 }}>
              {premium > 0 && <div style={{ flex: premium, background: '#7C3AED', borderRadius: 4 }} />}
              {pro > 0     && <div style={{ flex: pro,     background: '#D97706', borderRadius: 4 }} />}
              {free > 0    && <div style={{ flex: free,    background: '#1E3A5F', borderRadius: 4 }} />}
            </div>
            <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.6rem' }}>
              {[['Premium', premium, '#C084FC'], ['Pro', pro, '#FCD34D'], ['Free', free, '#475569']].map(([l, v, c]) => (
                <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: c }} />
                  <span style={{ color: '#94A3B8', fontSize: '0.72rem' }}>{l} <strong style={{ color: c }}>{v}</strong></span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div style={{
          display: 'flex', gap: '0.5rem', flexWrap: 'wrap',
          marginBottom: '1rem', alignItems: 'center',
          flexDirection: 'row'
        }}>
          <div style={{ position: 'relative', flex: '1 1 140px', minWidth: 0 }}>
            <Search size={14} color="#475569" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search name or email..."
              style={{
                width: '100%', padding: '0.55rem 0.75rem 0.55rem 2rem',
                background: '#0F172A', border: '1px solid #1E3A5F', borderRadius: 8,
                color: '#F1F5F9', fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box'
              }} />
          </div>
          {[
            { state: tierFilter, set: setTierFilter, opts: [['all','All Tiers'],['free','Free'],['pro','Pro'],['premium','Premium']] },
            { state: authFilter, set: setAuthFilter, opts: [['all','All Auth'],['email','Email'],['google','Google']] },
          ].map(({ state, set, opts }, i) => (
            <select key={i} value={state} onChange={e => set(e.target.value)} style={{
              padding: isMobile ? '0.45rem 0.4rem' : '0.55rem 0.75rem',
              background: '#0F172A', border: '1px solid #1E3A5F',
              borderRadius: 8, color: '#F1F5F9',
              fontSize: isMobile ? '0.75rem' : '0.85rem',
              outline: 'none', cursor: 'pointer', flexShrink: 0
            }}>
              {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          ))}
          <span style={{ color: '#475569', fontSize: '0.78rem', marginLeft: 'auto' }}>
            {filtered.length} of {total} users
          </span>
        </div>

        {/* User Table */}
        <div style={{
          background: 'linear-gradient(145deg,#0F172A,#1E293B)',
          border: '1px solid #1E293B', borderRadius: 12, overflow: 'hidden'
        }}>
          {loading ? (
            <div style={{ padding: '4rem', textAlign: 'center', color: '#475569' }}>
              <RefreshCw size={28} style={{ animation: 'spin 1s linear infinite', marginBottom: 12 }} />
              <div style={{ fontSize: '0.85rem' }}>Loading users...</div>
            </div>
          ) : users.length === 0 ? (
            <div style={{ padding: '4rem', textAlign: 'center' }}>
              <AlertCircle size={32} color="#334155" style={{ marginBottom: 12 }} />
              <div style={{ color: '#475569', fontSize: '0.875rem' }}>
                No users loaded. The <code style={{ color: '#60A5FA' }}>/api/users/all</code> endpoint may need to be added to your backend.
              </div>
              <div style={{ color: '#334155', fontSize: '0.78rem', marginTop: 8 }}>
                See backend setup instructions below.
              </div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? 600 : 800 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #1E3A5F' }}>
                    {[
                      ['#', 'id', '40px'],
                      ['User', 'name', 'auto'],
                      ['Email', 'email', 'auto'],
                      ['Tier', 'subscription_tier', '80px'],
                      ['Status', 'subscription_status', '80px'],
                      ...(isMobile ? [] : [
                        ['Auth', 'auth_provider', '70px'],
                        ['Joined', 'created_at', '110px'],
                        ['Expires', 'subscription_expires_at', '110px'],
                      ]),
                      ['Actions', null, '80px'],
                    ].map(([label, field, w]) => (
                      <th key={label} onClick={() => field && toggleSort(field)} style={{
                        padding: '0.75rem 1rem', textAlign: 'left', color: '#64748B',
                        fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em',
                        textTransform: 'uppercase', width: w, whiteSpace: 'nowrap',
                        cursor: field ? 'pointer' : 'default', userSelect: 'none'
                      }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                          {label} {field && <SortIcon field={field} />}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u, i) => (
                    <tr key={u.id} style={{
                      borderBottom: '1px solid #0F172A',
                      background: i % 2 === 0 ? 'transparent' : 'rgba(15,23,42,0.4)',
                      transition: 'background 0.15s'
                    }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(30,58,95,0.25)'}
                      onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'rgba(15,23,42,0.4)'}
                    >
                      <td style={{ padding: isMobile ? '0.5rem 0.4rem' : '0.7rem 1rem', color: '#475569', fontSize: '0.75rem' }}>{u.id}</td>
                      <td style={{ padding: '0.7rem 1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                          <div style={{
                            width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                            background: `hsl(${(u.id * 47) % 360}, 50%, 20%)`,
                            border: `1px solid hsl(${(u.id * 47) % 360}, 50%, 35%)`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.75rem', fontWeight: 700,
                            color: `hsl(${(u.id * 47) % 360}, 70%, 70%)`
                          }}>
                            {(u.name || u.email || '?')[0].toUpperCase()}
                          </div>
                          <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#E2E8F0' }}>
                            {u.name || '—'}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '0.7rem 1rem', fontSize: '0.8rem', color: '#94A3B8' }}>{u.email}</td>
                      <td style={{ padding: '0.7rem 1rem' }}><TierBadge tier={u.subscription_tier} /></td>
                      <td style={{ padding: '0.7rem 1rem' }}><StatusDot active={u.subscription_status === 'active'} /></td>
                      {!isMobile && <td style={{ padding: '0.7rem 0.5rem' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: '#64748B' }}>
                          {u.auth_provider === 'google' ? <Globe size={12} color="#34D399" /> : <Mail size={12} color="#60A5FA" />}
                          {u.auth_provider}
                        </span>
                      </td>}
                      {!isMobile && <td style={{ padding: '0.7rem 0.5rem', fontSize: '0.78rem', color: '#64748B' }}>
                        {timeAgo(u.created_at)}
                        <div style={{ fontSize: '0.68rem', color: '#334155' }}>{fmtDate(u.created_at)}</div>
                      </td>}
                      {!isMobile && <td style={{ padding: '0.7rem 0.5rem', fontSize: '0.78rem', color: u.subscription_expires_at ? '#86EFAC' : '#475569' }}>
                        {u.subscription_expires_at ? fmtDate(u.subscription_expires_at) : '—'}
                      </td>}
                      <td style={{ padding: '0.7rem 1rem' }}>
                        <button onClick={() => setGrantUser(u)} style={{
                          padding: '0.3rem 0.65rem', background: 'rgba(124,58,237,0.15)',
                          border: '1px solid rgba(124,58,237,0.3)', borderRadius: 6,
                          color: '#A78BFA', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600
                        }}>
                          <Crown size={11} style={{ marginRight: 3 }} />Grant
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Backend setup note if no users */}
        {!loading && users.length === 0 && (
          <div style={{
            marginTop: '1.5rem',
            background: 'rgba(30,58,95,0.2)', border: '1px solid #1E3A5F',
            borderRadius: 12, padding: '1.25rem'
          }}>
            <h4 style={{ color: '#60A5FA', margin: '0 0 0.75rem', fontSize: '0.875rem' }}>
              🔧 Backend Setup Required
            </h4>
            <p style={{ color: '#64748B', fontSize: '0.8rem', margin: '0 0 0.75rem' }}>
              Add this route to your <code style={{ color: '#94A3B8' }}>user.js</code> backend to enable user listing:
            </p>
            <pre style={{
              background: '#0F172A', border: '1px solid #1E3A5F', borderRadius: 8,
              padding: '0.875rem', fontSize: '0.75rem', color: '#94A3B8',
              overflow: 'auto', margin: 0
            }}>{`// GET /api/users/all — admin only
router.get('/all', async (req, res) => {
  const adminEmail = req.header('x-admin-email');
  const allowed = ['sandeep@investorq.io','aryansingh@investorq.io'];
  if (!allowed.includes(adminEmail)) return res.status(403).json({ error: 'Forbidden' });
  try {
    const result = await pool.query(
      \`SELECT id, email, name, subscription_tier, subscription_status,
        subscription_expires_at, auth_provider, created_at, phone
       FROM users ORDER BY created_at DESC\`
    );
    res.json({ users: result.rows });
  } catch (err) {
    res.status(500).json({ error: 'Failed to fetch users' });
  }
});`}</pre>
          </div>
        )}

      </div>

      {/* Grant Modal */}
      {grantUser && (
        <GrantModal
          user={grantUser}
          adminEmail={adminEmail}
          onClose={() => setGrantUser(null)}
          onGrant={fetchUsers}
        />
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        input::placeholder { color: #334155; }
        select option { background: #0F172A; color: #F1F5F9; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #0F172A; }
        ::-webkit-scrollbar-thumb { background: #1E3A5F; border-radius: 3px; }
      `}</style>
    </div>
  );
};

export default AdminPanel;
